import React, { useEffect, useRef, useState, createContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useAuthStore } from './store/auth';
import { extendSession, isSessionValid } from './utils/auth';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import ScrollToTop from './components/ScrollToTop';

// Components
import StoreHeader from './views/base/StoreHeader';
import StoreFooter from './views/base/StoreFooter';
import Home from './views/store/Home';
import Products from './views/store/Products';
import ProductDetail from './views/store/ProductDetail';
import Cart from './views/store/Cart';
import Checkout from './views/store/Checkout';
import PaymentSuccess from './views/store/PaymentSuccess';

import Login from './views/auth/Login';
import Register from './views/auth/Register';
import Logout from './views/auth/Logout';
import ForgotPassword from './views/auth/ForgotPassword';
import CreateNewPassword from './views/auth/CreatePassword';

import Account from './views/customer/Account';
import Orders from './views/customer/Orders';
import OrderDetail from './views/customer/OrderDetail';
import Wishlist from './views/customer/Wishlist';
import CustomerNotification from './views/customer/CustomerNotifications';
import Invoice from './views/customer/Invoice';
import CustomerSettings from './views/customer/Settings';

import Search from './views/store/Search';
import About from './views/store/About';
import Contact from './views/store/Contact';
import Privacy from './views/store/Privacy';
import Terms from './views/store/Terms';
import FAQ from './views/store/FAQ';
import Support from './views/store/Support';

import CleanChatbot from './components/CleanChatbot';
import WebsiteLoadingSkeleton from './components/WebsiteLoadingSkeleton';
import './components/MetallicLoadingEffect.css';

import apiInstance from './utils/axios';
import getCartID from './views/plugin/cartID';
import UserData from './views/plugin/UserData';

// Cart context
export const CartContext = createContext();

function AppContent() {
  const [cartCount, setCartCount] = useState(0);
  const [showCartNotification, setShowCartNotification] = useState(false);

  const cartId = getCartID();
  const userData = UserData();
  const prevCartCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const loading = useAuthStore((state) => state.loading);

  // Initialize auth
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Session persistence
  useEffect(() => {
    const interval = setInterval(async () => {
      if (isSessionValid()) {
        await extendSession();
      }
    }, 30 * 60 * 1000);

    const handleActivity = async () => {
      if (isSessionValid()) {
        await extendSession();
      }
    };

    window.addEventListener('focus', handleActivity);
    document.addEventListener('visibilitychange', handleActivity);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleActivity);
      document.removeEventListener('visibilitychange', handleActivity);
    };
  }, []);

  useEffect(() => {
    if (cartId && !isInitialLoadRef.current) {
      fetchCartCount();
    }
  }, [cartId]);

  useEffect(() => {
    if (cartId) {
      fetchCartCount();
      isInitialLoadRef.current = false;
    }
  }, [cartId]);

  const fetchCartCount = async () => {
    try {
      if (!cartId) return;

      const url = userData?.user_id
        ? `cart-list/${cartId}/${userData.user_id}/`
        : `cart-list/${cartId}/`;

      const response = await apiInstance.get(url);
      const count = response.data.cart_items?.length || 0;

      setCartCount(count);

      if (count > prevCartCountRef.current && prevCartCountRef.current > 0) {
        setShowCartNotification(true);
        setTimeout(() => setShowCartNotification(false), 3000);
      }

      prevCartCountRef.current = count;
    } catch {
      setCartCount(0);
    }
  };

  const updateCartCountAndNotify = (newCount) => {
    setCartCount(newCount);
    if (newCount > prevCartCountRef.current) {
      setShowCartNotification(true);
      setTimeout(() => setShowCartNotification(false), 3000);
    }
    prevCartCountRef.current = newCount;
  };

  if (loading) {
    return <WebsiteLoadingSkeleton />;
  }

  return (
    <CartContext.Provider value={[cartCount, updateCartCountAndNotify]}>
      <div className="loading-complete">
        <BrowserRouter>
          <ScrollToTop />
          <StoreHeader />

          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products/" element={<Products />} />
            <Route path="/detail/:slug/" element={<ProductDetail />} />
            <Route path="/detail/:slug/:color/:size/" element={<ProductDetail />} />
            <Route path="/cart/" element={<Cart />} />
            <Route path="/carts" element={<Navigate to="/cart/" replace />} />
            <Route path="/carts/" element={<Navigate to="/cart/" replace />} />
            <Route path="/checkout/:order_oid/" element={<Checkout />} />
            <Route path="/payment-success/:order_oid/" element={<PaymentSuccess />} />

            <Route path="/login/" element={<Login />} />
            <Route path="/register/" element={<Register />} />
            <Route path="/logout/" element={<Logout />} />
            <Route path="/forgot-password/" element={<ForgotPassword />} />
            <Route path="/create-new-password/" element={<CreateNewPassword />} />

            <Route path="/customer/account/" element={<Account />} />
            <Route path="/customer/orders/" element={<Orders />} />
            <Route path="/customer/orders/:order_oid/" element={<OrderDetail />} />
            <Route path="/customer/wishlist/" element={<Wishlist />} />
            <Route path="/customer/notifications/" element={<CustomerNotification />} />
            <Route path="/customer/invoice/:order_oid/" element={<Invoice />} />
            <Route path="/customer/settings/" element={<CustomerSettings />} />

            <Route path="/search/" element={<Search />} />
            <Route path="/about/" element={<About />} />
            <Route path="/contact/" element={<Contact />} />
            <Route path="/privacy/" element={<Privacy />} />
            <Route path="/terms/" element={<Terms />} />
            <Route path="/faq/" element={<FAQ />} />
            <Route path="/support/" element={<Support />} />
          </Routes>

          <StoreFooter />
          <Analytics />
          <SpeedInsights />
          <CleanChatbot />
          
          {/* WhatsApp Floating Button */}
          <a
            href="https://wa.me/45999456009"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '85px',
              backgroundColor: '#25D366',
              color: 'white',
              borderRadius: '50%',
              width: '60px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              zIndex: 1000,
              transition: 'all 0.3s ease',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.1)';
              e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
            }}
          >
            <i className="fab fa-whatsapp" style={{ fontSize: '28px' }}></i>
          </a>
        </BrowserRouter>

        {showCartNotification && cartCount > 0 && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#10b981',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            zIndex: 9999,
            fontWeight: 600
          }}>
            🛒 {cartCount} {cartCount === 1 ? 'item' : 'items'} in cart
          </div>
        )}
      </div>
    </CartContext.Provider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AppContent />
    </HelmetProvider>
  );
}

export default App;
